import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api } from "../api/client";
import { useSetting } from "../settings/SettingsContext";
import { describeNextStop, nextLiveStop } from "../api/liveProgress";
import type {
  Driver,
  ListResponse,
  Route,
  Trip,
  TripInstance,
  TripInstanceLiveProgress,
  TripInstanceStatus,
  TripInstanceStopTime,
  Vehicle,
} from "../api/types";

function unwrap<T>(value: ListResponse<T> | T[] | undefined | null): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  const d = (value as ListResponse<T>).data;
  return Array.isArray(d) ? d : [];
}

// ISO timestamp → value for <input type="datetime-local"> (local wall time).
function toLocalInput(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function tripLabel(t: Trip): string {
  return (
    t.trip_headsign ||
    t.trip_short_name ||
    `Trip ${t.trip_id.slice(0, 8)}…`
  );
}

function driverLabel(d: Driver): string {
  const name = `${d.first_name} ${d.last_name}`.trim();
  return name ? `${name} (${d.employee_id})` : d.employee_id;
}

function vehicleLabel(v: Vehicle): string {
  return v.vehicle_label || v.license_plate || v.vehicle_id;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "scheduled,in_progress", label: "Active (scheduled + in-progress)" },
  { value: "in_progress", label: "In progress only" },
  { value: "scheduled", label: "Scheduled only" },
  { value: "completed", label: "Completed" },
  { value: "incomplete", label: "Incomplete (never reached the end)" },
  { value: "cancelled,no_show", label: "Cancelled / No-show" },
  { value: "", label: "All statuses" },
];

function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fmtTime(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function fmtDelay(seconds?: number | null, warn = 60, crit = 300): { text: string; tone: "ok" | "warn" | "crit" | "info" } {
  if (seconds == null) return { text: "—", tone: "info" };
  const abs = Math.abs(seconds);
  const mins = Math.round(abs / 60);
  const sign = seconds >= 0 ? "+" : "−";
  let tone: "ok" | "warn" | "crit" = "ok";
  if (abs > crit) tone = "crit";
  else if (abs > warn) tone = "warn";
  const text = abs < 60 ? `${sign}${abs}s` : `${sign}${mins}m`;
  return { text, tone };
}

function statusBadgeTone(s: TripInstanceStatus): "ok" | "warn" | "crit" | "info" {
  switch (s) {
    case "in_progress": return "ok";
    case "scheduled": return "info";
    case "completed": return "info";
    case "cancelled": return "warn";
    case "incomplete": return "warn";
    case "no_show": return "crit";
  }
}

// ── Presentation helpers for the redesigned table (all derived from real
// TripInstance fields — nothing hardcoded per route/driver). ────────────────

function fmtServiceDate(iso: string): string {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** "Orange Line" -> "OL", "Blue Line" -> "BL". Generic — no per-route hardcoding. */
function routeCode(name?: string): string {
  if (!name) return "—";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/** Multi-word station name -> initials ("Faiz Ahmed Faiz" -> "FAF"); short
 *  names (e.g. "N-5") pass through unchanged. */
function abbreviateStation(text: string): string {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return text.trim();
  return words.map((w) => w[0]).join("").toUpperCase();
}

/** "Faiz Ahmed Faiz to N-5" -> "FAF → N-5", derived from the real
 *  route_long_name; falls back to headsign/short name when it doesn't
 *  follow the "X to Y" convention. */
function routeDirectionLabel(t: TripInstance): string {
  const src = t.route_long_name;
  if (src) {
    const m = src.match(/^(.*?)\s+to\s+(.*)$/i);
    if (m) return `${abbreviateStation(m[1])} → ${abbreviateStation(m[2])}`;
    return src;
  }
  return t.headsign || t.route_short_name || "—";
}

function tripProgress(t: TripInstance): { current: number; total: number; pct: number } {
  // total_stops is real-and-cheap on both list and detail responses;
  // stop_times.length only exists once the detail endpoint has hydrated it.
  const total = t.total_stops ?? t.stop_times?.length ?? 0;
  const current = Math.min(t.current_stop_sequence ?? 0, total);
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return { current, total, pct };
}

/** Derived, not a distinct backend status: an in-progress trip still at or
 *  before its first stop reads as "Boarding" rather than "In progress". */
function statusLabel(t: TripInstance): { text: string; dot: "ok" | "info" | "warn" | "crit" } {
  if (t.status === "in_progress") {
    if ((t.current_stop_sequence ?? 0) <= 1) return { text: "Boarding", dot: "info" };
    return { text: "In progress", dot: "ok" };
  }
  if (t.status === "scheduled") return { text: "Scheduled", dot: "info" };
  if (t.status === "completed") return { text: "Completed", dot: "info" };
  if (t.status === "cancelled") return { text: "Cancelled", dot: "warn" };
  return { text: "No show", dot: "crit" };
}

/** Delay text tone: only "actually late" (past the warn threshold) reads as
 *  amber — on-time and early both read as the good/teal tone. */
function delayCellTone(seconds: number | null | undefined, warnSec: number): "ok" | "warn" {
  if (seconds == null) return "ok";
  return seconds > warnSec ? "warn" : "ok";
}

function delayCellText(seconds: number | null | undefined, warnSec: number): string {
  if (seconds == null) return "—";
  if (Math.abs(seconds) <= warnSec) return "on time";
  const mins = Math.round(Math.abs(seconds) / 60);
  return `${seconds > 0 ? "+" : "−"}${mins} min`;
}

export default function TripsPage() {
  const refreshMs = useSetting<number>("occ.trips_refresh_ms", 5000);
  const lateWarnSec = useSetting<number>("occ.late_warn_seconds", 60);
  const lateCritSec = useSetting<number>("occ.late_crit_seconds", 300);
  const [serviceDate, setServiceDate] = useState<string>(todayIso());
  const [statusFilter, setStatusFilter] = useState<string>(STATUS_OPTIONS[0].value);
  const [trips, setTrips] = useState<TripInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TripInstance | null>(null);
  const [live, setLive] = useState<TripInstanceLiveProgress | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<TripInstance | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await api.listTripInstances({
        service_date: serviceDate || undefined,
        status: statusFilter || undefined,
      });
      setTrips(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load trips");
    } finally {
      setLoading(false);
    }
  }, [serviceDate, statusFilter]);

  useEffect(() => {
    setLoading(true);
    load();
    const id = window.setInterval(load, refreshMs);
    return () => window.clearInterval(id);
  }, [load, refreshMs]);

  // Auto-refresh detail drawer while open.
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setLive(null);
      return;
    }
    let cancelled = false;
    const loadDetail = async () => {
      try {
        const ti = await api.getTripInstance(selectedId);
        if (!cancelled) setDetail(ti);
      } catch {
        if (!cancelled) setDetail(null);
      }
      // Live progress merges GPS check-ins, manual stop events and the latest
      // fix — the only source that knows the next stop. Optional: the drawer
      // still renders from the trip instance if this fails.
      try {
        const lp = await api.getTripLiveProgress(selectedId);
        if (!cancelled) setLive(lp);
      } catch {
        if (!cancelled) setLive(null);
      }
    };
    loadDetail();
    const id = window.setInterval(loadDetail, refreshMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [selectedId, refreshMs]);

  const summary = useMemo(() => {
    let inProgress = 0, scheduled = 0, completed = 0, late = 0;
    for (const t of trips) {
      if (t.status === "in_progress") inProgress++;
      else if (t.status === "scheduled") scheduled++;
      else if (t.status === "completed") completed++;
      if ((t.delay_seconds ?? 0) > lateCritSec) late++;
    }
    return { inProgress, scheduled, completed, late, total: trips.length };
  }, [trips, lateCritSec]);

  const onCancel = useCallback(async (id: string) => {
    if (!window.confirm("Cancel this trip instance?")) return;
    try {
      await api.cancelTripInstance(id, "Cancelled from OCC");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Cancel failed");
    }
  }, [load]);

  const onComplete = useCallback(async (id: string) => {
    if (!window.confirm("Mark this trip as completed?")) return;
    try {
      await api.endTripInstance(id, { notes: "Completed from OCC" });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Complete failed");
    }
  }, [load]);

  const staleTrips = useMemo(() => {
    const now = Date.now();
    return trips.filter(
      (t) => t.status === "in_progress" && t.scheduled_end && new Date(t.scheduled_end).getTime() < now - 30 * 60_000
    );
  }, [trips]);

  const onCompleteAllStale = useCallback(async () => {
    if (staleTrips.length === 0) return;
    if (!window.confirm(`Complete ${staleTrips.length} stale in-progress trip(s)?`)) return;
    let ok = 0, fail = 0;
    for (const t of staleTrips) {
      try {
        await api.endTripInstance(t.id, { notes: "Bulk-completed stale trip from OCC" });
        ok++;
      } catch {
        fail++;
      }
    }
    setError(fail > 0 ? `Completed ${ok}, failed ${fail}` : null);
    await load();
  }, [staleTrips, load]);

  return (
    <div className="page trips-page">
      <div className="trips-header">
        <div>
          <h1>Trips</h1>
          <p className="secondary-text">Today's service — scheduled, running and completed trips.</p>
        </div>
        <button className="btn primary trips-new-btn" type="button" onClick={() => setShowNew(true)}>
          + New trip
        </button>
      </div>

      <div className="card trips-filter-card">
        <label className="trips-field">
          <span>Service date</span>
          <input
            type="date"
            value={serviceDate}
            onChange={(e) => setServiceDate(e.target.value)}
          />
        </label>
        <label className="trips-field">
          <span>Status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>
        <div className="trips-stat-pills">
          <span className="trips-stat-pill neutral">TOTAL {summary.total}</span>
          <span className="trips-stat-pill ok">IN PROGRESS {summary.inProgress}</span>
          <span className="trips-stat-pill info">SCHEDULED {summary.scheduled}</span>
          <span className={`trips-stat-pill ${summary.late > 0 ? "warn" : "neutral"}`}>LATE &gt;5M {summary.late}</span>
          {staleTrips.length > 0 && (
            <button
              className="btn ghost danger"
              type="button"
              onClick={onCompleteAllStale}
              title="Complete all in-progress trips past their scheduled end by 30+ minutes"
            >
              Complete {staleTrips.length} stale
            </button>
          )}
        </div>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      <div className="card trips-table-card">
        <table className="data-table trips-table">
          <thead>
            <tr>
              <th>Driver / Vehicle</th>
              <th>Route</th>
              <th>Scheduled</th>
              <th>Progress</th>
              <th>Status</th>
              <th>Delay</th>
              <th className="col-actions" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading && trips.length === 0 && (
              <tr><td colSpan={7} className="empty">Loading…</td></tr>
            )}
            {!loading && trips.length === 0 && (
              <tr><td colSpan={7} className="empty">No trips for {fmtServiceDate(serviceDate)}.</td></tr>
            )}
            {trips.map((t) => {
              const progress = tripProgress(t);
              const status = statusLabel(t);
              const delayTone = delayCellTone(t.delay_seconds, lateWarnSec);
              const driverName = t.driver_name || (t.driver_id ? `${t.driver_id.slice(0, 8)}…` : "Unassigned");
              const routeColor = t.route_color ? `#${t.route_color}` : "var(--brand-primary)";
              const routeTextColor = t.route_text_color ? `#${t.route_text_color}` : "#fff";
              return (
                <tr
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className={"trips-row" + (t.id === selectedId ? " is-selected" : "")}
                >
                  <td>
                    <div className="trips-driver-cell">
                      <span className="trips-avatar">{initials(driverName)}</span>
                      <div className="trips-driver-info">
                        <span className="trips-driver-name">{driverName}</span>
                        <span className="trips-vehicle-code mono">{t.vehicle_label || "—"}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="trips-route-pill" style={{ background: routeColor, color: routeTextColor }}>
                      <span className="trips-route-code">{routeCode(t.route_short_name)}</span>
                      <span className="trips-route-dir">{routeDirectionLabel(t)}</span>
                    </span>
                  </td>
                  <td>
                    <div>{fmtTime(t.scheduled_start)} - {fmtTime(t.scheduled_end)}</div>
                    <div className="secondary-text">
                      {t.actual_start
                        ? `claimed ${fmtTimeSec(t.actual_start)} (${fmtClaimDelayMinutes(claimDelaySeconds(t))})${t.actual_end ? ` · ended ${fmtTime(t.actual_end)}` : ""}`
                        : "not claimed"}
                    </div>
                  </td>
                  <td>
                    <div className="trips-progress-cell">
                      <div className="trips-progress-track">
                        <div
                          className={`trips-progress-fill ${delayTone}`}
                          style={{ width: `${Math.max(progress.pct, progress.total > 0 ? 4 : 0)}%` }}
                        />
                      </div>
                      <div className="trips-progress-meta">
                        <span className="secondary-text">{progress.total > 0 ? `${progress.current}/${progress.total} stops` : "—"}</span>
                        <span className="secondary-text">{progress.total > 0 ? `${progress.pct}%` : ""}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="trips-status-cell">
                      <span className={`dot sev-${status.dot}`} />
                      {status.text}
                    </span>
                  </td>
                  <td>
                    <span className={`trips-delay-text ${delayCellTone(t.delay_seconds, lateWarnSec)}`}>
                      {t.status === "scheduled" ? "—" : delayCellText(t.delay_seconds, lateWarnSec)}
                    </span>
                  </td>
                  <td className="col-actions">
                    <div className="trips-row-actions">
                      <button
                        className="btn ghost"
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setEditing(t); }}
                      >
                        Edit
                      </button>
                      {t.status === "in_progress" && (
                        <button
                          className="btn ghost"
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onComplete(t.id); }}
                        >
                          Complete
                        </button>
                      )}
                      {(t.status === "in_progress" || t.status === "scheduled") && (
                        <button
                          className="btn ghost danger"
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onCancel(t.id); }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <TripDrawer
          trip={detail}
          live={live?.trip_instance_id === selectedId ? live : null}
          onClose={() => setSelectedId(null)}
        />
      )}

      {showNew && (
        <NewTripModal
          defaultDate={serviceDate || todayIso()}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}

      {editing && (
        <EditTripModal
          trip={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// Centered modal overlay — self-contained so it doesn't depend on a CSS
// class the dashboard doesn't ship.
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(520px, 92vw)", maxHeight: "88vh", overflowY: "auto" }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn ghost" type="button" onClick={onClose}>Close</button>
        </header>
        {children}
      </div>
    </div>
  );
}

function NewTripModal({ defaultDate, onClose, onCreated }: { defaultDate: string; onClose: () => void; onCreated: () => void }) {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [routeId, setRouteId] = useState("");
  const [tripId, setTripId] = useState("");
  const [startLocal, setStartLocal] = useState(`${defaultDate}T08:00`);
  const [endLocal, setEndLocal] = useState(`${defaultDate}T08:30`);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.listRoutes().then((r) => setRoutes(unwrap(r))).catch(() => setRoutes([]));
  }, []);

  useEffect(() => {
    setTripId("");
    api
      .listTrips({ route_id: routeId || undefined, limit: 500 })
      .then((r) => setTrips(unwrap(r)))
      .catch(() => setTrips([]));
  }, [routeId]);

  const submit = async () => {
    if (!tripId) { setErr("Pick a trip."); return; }
    const startMs = Date.parse(startLocal);
    if (Number.isNaN(startMs)) { setErr("Start time is required."); return; }
    const endMs = endLocal ? Date.parse(endLocal) : NaN;
    if (!Number.isNaN(endMs) && endMs < startMs) { setErr("End must be after start."); return; }
    setSaving(true); setErr(null);
    try {
      await api.createTripInstance({
        trip_id: tripId,
        service_date: startLocal.slice(0, 10),
        scheduled_start: startMs,
        scheduled_end: Number.isNaN(endMs) ? undefined : endMs,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="New trip" onClose={onClose}>
      <p className="secondary-text" style={{ marginTop: 0 }}>
        Creates one open-pool trip (no driver or bus assigned).
      </p>
      {err && <div className="banner banner-error">{err}</div>}
      <div style={{ display: "grid", gap: 12 }}>
        <label style={{ display: "grid", gap: 4 }}>
          Route (filter)
          <select value={routeId} onChange={(e) => setRouteId(e.target.value)}>
            <option value="">All routes</option>
            {routes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.route_short_name ? `${r.route_short_name} · ` : ""}{r.route_long_name}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          Trip
          <select value={tripId} onChange={(e) => setTripId(e.target.value)}>
            <option value="">— Select a trip —</option>
            {trips.map((t) => (
              <option key={t.id} value={t.id}>{tripLabel(t)}</option>
            ))}
          </select>
        </label>
        <div style={{ display: "flex", gap: 12 }}>
          <label style={{ display: "grid", gap: 4, flex: 1 }}>
            Start
            <input type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 4, flex: 1 }}>
            End
            <input type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} />
          </label>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn ghost" type="button" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn" type="button" onClick={submit} disabled={saving}>
          {saving ? "Creating…" : "Create"}
        </button>
      </div>
    </Modal>
  );
}

function EditTripModal({ trip, onClose, onSaved }: { trip: TripInstance; onClose: () => void; onSaved: () => void }) {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [startLocal, setStartLocal] = useState(toLocalInput(trip.scheduled_start));
  const [endLocal, setEndLocal] = useState(toLocalInput(trip.scheduled_end));
  const [driverId, setDriverId] = useState(trip.driver_id ?? "");
  const [vehicleId, setVehicleId] = useState(trip.vehicle_id ?? "");
  const [notes, setNotes] = useState(trip.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.listDrivers().then((r) => setDrivers(unwrap(r))).catch(() => setDrivers([]));
    api.listVehicles().then((r) => setVehicles(unwrap(r))).catch(() => setVehicles([]));
  }, []);

  const submit = async () => {
    const startMs = startLocal ? Date.parse(startLocal) : NaN;
    const endMs = endLocal ? Date.parse(endLocal) : NaN;
    if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs < startMs) {
      setErr("End must be after start."); return;
    }
    setSaving(true); setErr(null);
    try {
      await api.updateTripInstance(trip.id, {
        scheduled_start: Number.isNaN(startMs) ? undefined : startMs,
        scheduled_end: Number.isNaN(endMs) ? undefined : endMs,
        driver_id: driverId,   // "" clears → open pool
        vehicle_id: vehicleId, // "" clears → open pool
        notes,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Edit · ${trip.route_short_name ? `${trip.route_short_name} ` : ""}${trip.headsign || trip.route_long_name || trip.trip_id.slice(0, 8)}`}
      onClose={onClose}
    >
      {err && <div className="banner banner-error">{err}</div>}
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", gap: 12 }}>
          <label style={{ display: "grid", gap: 4, flex: 1 }}>
            Scheduled start
            <input type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 4, flex: 1 }}>
            Scheduled end
            <input type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} />
          </label>
        </div>
        <label style={{ display: "grid", gap: 4 }}>
          Driver
          <select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
            <option value="">— Open pool (no driver) —</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>{driverLabel(d)}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          Vehicle
          <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">— Open pool (no bus) —</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{vehicleLabel(v)}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          Notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </label>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn ghost" type="button" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn" type="button" onClick={submit} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

/**
 * How late (positive) or early the trip was claimed against the timetable.
 * The timetable time is the first stop's scheduled *arrival*; scheduled_start
 * is its departure, i.e. the timetable time plus the first stop's dwell.
 */
function claimReference(t: TripInstance): string | undefined {
  const first = (t.stop_times ?? []).reduce<TripInstanceStopTime | undefined>(
    (min, s) => (!min || s.stop_sequence < min.stop_sequence ? s : min), undefined);
  return first?.scheduled_arrival ?? t.scheduled_start;
}

function claimDelaySeconds(t: TripInstance): number | null {
  const ref = claimReference(t);
  if (!t.actual_start || !ref) return null;
  const secs = (new Date(t.actual_start).getTime() - new Date(ref).getTime()) / 1000;
  return Number.isFinite(secs) ? Math.round(secs) : null;
}

/** Minute resolution, for the list: it only has scheduled_start, which carries
 *  the first-stop dwell, so seconds there would be noise. */
function fmtClaimDelayMinutes(secs: number | null): string {
  if (secs == null) return "—";
  const m = Math.round(secs / 60);
  return m === 0 ? "on time" : `${m > 0 ? "+" : "−"}${Math.abs(m)}m`;
}

function fmtAge(seconds: number): string {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} days`;
}

function fmtTimeSec(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function TripDrawer({
  trip,
  live,
  onClose,
}: {
  trip: TripInstance | null;
  live: TripInstanceLiveProgress | null;
  onClose: () => void;
}) {
  if (!trip) {
    return (
      <aside className="drawer">
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>Trip details</h3>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </header>
        <div className="empty">Loading…</div>
      </aside>
    );
  }

  const stops = (trip.stop_times ?? []).slice().sort((a, b) => a.stop_sequence - b.stop_sequence);
  // GPS check-ins live in stop_visits, not stop_times, so a stop the app
  // checked into automatically only shows as reached via live progress.
  const liveBySeq = new Map((live?.stops ?? []).map((s) => [s.stop_sequence, s]));
  const next = nextLiveStop(live);
  const running = trip.status === "in_progress";
  const reachedCount = live ? live.stops.filter((s) => s.status !== "upcoming").length : 0;

  return (
    <aside className="drawer">
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3>
          {trip.route_short_name ? `${trip.route_short_name} · ` : ""}
          {trip.headsign || trip.route_long_name || `Trip ${trip.trip_id.slice(0, 8)}…`}
        </h3>
        <button className="btn ghost" onClick={onClose}>Close</button>
      </header>

      <dl className="kv-grid">
        <dt>Status</dt>
        <dd><span className={`pill sev-${statusBadgeTone(trip.status)}`}>{trip.status.replace("_", " ")}</span></dd>
        <dt>Service date</dt>
        <dd>{trip.service_date.slice(0, 10)}</dd>
        <dt>Driver</dt>
        <dd>{trip.driver_name || trip.driver_id || "—"}</dd>
        <dt>Vehicle</dt>
        <dd>{trip.vehicle_label || trip.vehicle_id || "—"}</dd>
        <dt>Scheduled</dt>
        <dd>{fmtTime(trip.scheduled_start)} → {fmtTime(trip.scheduled_end)}</dd>
        <dt>Claimed at</dt>
        <dd>
          {fmtTimeSec(trip.actual_start)}
          {(() => {
            const cd = claimDelaySeconds(trip);
            if (cd == null) return null;
            const d = fmtDelay(cd);
            return <> <span className={`pill sev-${d.tone}`}>{cd === 0 ? "on time" : d.text}</span> vs {fmtTimeSec(claimReference(trip))}</>;
          })()}
        </dd>
        <dt>Actual</dt>
        <dd>{fmtTime(trip.actual_start)} → {fmtTime(trip.actual_end)}</dd>
        <dt>Delay</dt>
        <dd>
          {(() => {
            const secs = live?.delay_seconds ?? trip.delay_seconds;
            const d = fmtDelay(secs);
            return <span className={`pill sev-${d.tone}`}>{secs === 0 ? "on time" : d.text}</span>;
          })()}
        </dd>
        <dt>Current stop</dt>
        <dd>
          {live?.current_stop_label
            ? `${live.current_stop_label} (${reachedCount} / ${live.stops.length})`
            : trip.current_stop_sequence != null && stops.length > 0
              ? `${trip.current_stop_sequence} / ${stops.length}`
              : "—"}
        </dd>
        {running && (
          <>
            <dt>Next stop</dt>
            <dd>{describeNextStop(next) ?? (live && live.stops.length > 0 ? "Final stop reached" : "—")}</dd>
            <dt>Live GPS</dt>
            <dd>
              {live?.is_live
                ? `Live${live.live_speed_kmh != null ? ` · ${Math.round(live.live_speed_kmh)} km/h` : ""}`
                : live?.live_location_age_seconds != null
                  ? `Stale · last fix ${fmtAge(live.live_location_age_seconds)} ago`
                  : "No fix"}
            </dd>
          </>
        )}
      </dl>

      <h4 style={{ marginTop: 16 }}>Stop progress</h4>
      <table className="data-table compact">
        <thead>
          <tr>
            <th>#</th>
            <th>Stop</th>
            <th>Sched arr</th>
            <th>Actual arr</th>
            <th>Sched dep</th>
            <th>Actual dep</th>
            <th>Delay</th>
            <th>Src</th>
          </tr>
        </thead>
        <tbody>
          {stops.length === 0 && (
            <tr><td colSpan={8} className="empty">No stop progress yet.</td></tr>
          )}
          {stops.map((s: TripInstanceStopTime) => {
            const ls = liveBySeq.get(s.stop_sequence);
            const actualArr = s.actual_arrival ?? ls?.check_in_time;
            const actualDep = s.actual_departure ?? ls?.check_out_time;
            const arrDelaySecs = s.arrival_delay_seconds ?? ls?.arrival_delay_seconds;
            const arrDelay = arrDelaySecs === 0 ? { text: "on time", tone: "ok" as const } : fmtDelay(arrDelaySecs);
            const isNext = next?.stop_sequence === s.stop_sequence;
            const source = s.arrival_source || s.departure_source || (ls?.check_in_time ? "gps" : "");
            return (
              <tr key={s.id} className={(actualArr ? "" : "is-pending") + (isNext ? " is-selected" : "")}>
                <td>{s.stop_sequence}</td>
                <td>
                  <div>{s.stop_name || `${s.stop_id.slice(0, 8)}…`}{isNext ? " · next" : ""}</div>
                  {s.stop_code ? <div className="secondary-text">#{s.stop_code}</div> : null}
                </td>
                <td>{fmtTime(s.scheduled_arrival)}</td>
                <td>{actualArr ? fmtTime(actualArr) : isNext && next?.predicted_arrival ? `ETA ${fmtTime(next.predicted_arrival)}` : "—"}</td>
                <td>{fmtTime(s.scheduled_departure)}</td>
                <td>{fmtTime(actualDep)}</td>
                <td><span className={`pill sev-${arrDelay.tone}`}>{arrDelay.text}</span></td>
                <td className="secondary-text">{source || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </aside>
  );
}
